/**
 * Thangka palette (PLAN.md §4.1) as linear-ish RGB triples for uniforms, plus the master-colour ramp.
 * Pure: no GL, fully unit-testable.
 */
import type { Family } from '@tonist/shared';

export type RGB = [number, number, number];

export function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export const COLOURS = {
  ground: hexToRgb('#0B0A0F'),
  bone: hexToRgb('#F3EEE3'),
  gold: hexToRgb('#D4AF37'),
  goldHi: hexToRgb('#FFE08A'),
  goldShadow: hexToRgb('#8C6D1F'),
  cinnabar: hexToRgb('#B3261E'),
  vermilion: hexToRgb('#E34234'),
  azurite: hexToRgb('#1F3A93'),
  lapis: hexToRgb('#26619C'),
  malachite: hexToRgb('#1D8A6B'),
  malachiteHi: hexToRgb('#3EB489'),
  orpiment: hexToRgb('#E8B830'),
} as const;

/** Buddha-family zone hues (Vajradhatu): white centre, blue east, yellow south, red west, green north. */
export const FAMILY_RGB: Record<Family, RGB> = {
  white: COLOURS.bone,
  blue: COLOURS.azurite,
  yellow: COLOURS.orpiment,
  red: COLOURS.cinnabar,
  green: COLOURS.malachite,
};

/** Brighter variants used for glow / waveform lines so they read on the lamp-black ground. */
export const FAMILY_GLOW_RGB: Record<Family, RGB> = {
  white: hexToRgb('#FFF6E0'),
  blue: hexToRgb('#4F7BE8'),
  yellow: hexToRgb('#FFD25A'),
  red: hexToRgb('#F0553F'),
  green: hexToRgb('#4FD1A0'),
};

export const FAMILY_INDEX: Record<Family, number> = { white: 0, blue: 1, yellow: 2, red: 3, green: 4 };

/** Master centroid → 0..1 across ~5 octaves from 110 Hz. */
export function masterRampPosition(centroidHz: number): number {
  if (!(centroidHz > 0) || !Number.isFinite(centroidHz)) return 0;
  const oct = Math.log2(centroidHz / 110);
  return Math.min(1, Math.max(0, oct / 5));
}

const RAMP: RGB[] = [COLOURS.lapis, COLOURS.malachiteHi, COLOURS.orpiment, COLOURS.vermilion, hexToRgb('#FFF6E0')];

/** Family hue ramp blue → green → yellow → red → white for tinting highlights. */
export function rampColour(pos: number, out: RGB = [0, 0, 0]): RGB {
  const t = Math.min(1, Math.max(0, pos)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(t));
  const f = t - i;
  const a = RAMP[i]!, b = RAMP[i + 1]!;
  out[0] = a[0] + (b[0] - a[0]) * f;
  out[1] = a[1] + (b[1] - a[1]) * f;
  out[2] = a[2] + (b[2] - a[2]) * f;
  return out;
}

/** Sunburst target opacity for a voice count: 0 below 3 voices, 4 % at 3, rising to 8 % by 6. */
export function sunburstFor(voiceCount: number): number {
  if (voiceCount < 3) return 0;
  return Math.min(0.08, 0.04 + (voiceCount - 3) * (0.04 / 3));
}

/** Nebula contrast multiplier: 1 below 3 voices, up to 1.6 with a full chord. */
export function nebulaContrastFor(voiceCount: number): number {
  if (voiceCount < 3) return 1;
  return Math.min(1.6, 1 + 0.2 * (voiceCount - 2));
}
